import React, { useState, useEffect } from "react";
import useEditLoad from "../../hooks/useEditLoad";
import EditLoadGate from "../../components/editLoadGate";
import axios from "axios";
import Modal from "react-modal";
import Select from "../../components/select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import useForm from "../../hooks/books/useForm";
import validate from "../../validation/bookValidationRules";
import PreviewImage from "../../components/image/previewImage";
import LanguageSelector from "../../components/languageSelector";
import { useLanguage } from "../../context/languageContext";
import SearchAuthor from "./searchAuthor";
import Button from "../../components/button";
import SummaryEditor from "../../components/textEditor/editor";
import { normalizeCoverImage, convertToCloudFrontUrl, plainTextLength, isBlankHtml } from "../../helper";


const EditCategoryPopup = ({ bookId, isModalVisible, closeModal, onSuccess }) => {
    const { t } = useTranslation();
    const{ selectedLang, adminInfo, initialLangRef, setSelectedLang, fallbackLang } = useLanguage();

    const [particularData, setParticularData] = useState({});
    // Shows, and saves, only the book that was opened — see hooks/useEditLoad.
    const load = useEditLoad();
    const ready = Boolean(isModalVisible && load.isLoadedFor(bookId));

    // Every OPEN loads the saved book, not only a change of id: the popup
    // stays mounted, so reopening a book after Cancel showed the cancelled edits.
    useEffect(() => {
      if (bookId && isModalVisible) {
        load.begin(bookId);
        setSelectedLang(fallbackLang);
        initialLangRef.current = false;
        fetchCategoryData(String(bookId));
      }
      if (!isModalVisible) {
        setParticularData({});
        load.reset();
      }
    }, [bookId, isModalVisible]);

  const handleEditCategory = async () => {
    // Never save a form that is not showing this book.
    if (!ready) return;
    try {
      setIsSubmit(true);
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const { data: response } = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/books/update/${bookId}`, { values }, { headers });

      if (response.status === true) {
        onSuccess();
        toast.success("Book has been updated.");
      } else {
        toast.error(response?.message, {
          position: "top-center"
        });
      }
    } catch (error) {
      console.error("handleEditCategory catch error::", error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error;
      toast.error(errorMessage);
    } finally {
      setIsSubmit(false);
    }
  };

    const {
      values,
      setValues,
      isLoading,
      isSubmit,
      setIsSubmit,
      errors,
      editMediaError,
      languages,
      authorsData,
      allGenresOptions,
      dropdownRef,
      isShowAuthor,
      searchAuthorName,
      handleInputChange,
      handleAuthorSelect,
      handleRemoveAuthor,
      handleDropdownFocus,
  
      handleGetTranslationByLang,
      handleSelectLanguage,
      handleUpdateTranslationField,
      handleAddValues,
      handleUploadMedia,
      handleSubmit,
    } = useForm(handleEditCategory, validate, isModalVisible);
    
    const fetchCategoryData = async (id) => {
        if (!id) return;
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(`${process.env.REACT_APP_API_ROOT}/api/admin/books/get/${id}`, { headers });

            if (response?.status && load.accepts(id, response.data)) {
                setParticularData(response.data);
            } else {
                load.fail(id, response?.message || "");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            load.fail(id, error);
        }
    };

    useEffect(() => {
      if (particularData && particularData.translations && !initialLangRef.current) {
        const mergedTranslations = languages.map((lang) => {

          return (
            particularData.translations.find((t) => t.langCode === lang) || {
              langCode: lang,
              title: "",
              tagline: "",
              author: [],
              publishedBy: "",
              summary: "",
              type: "NonFiction",
              genre: [],
              link: "",
            }
          );
        });
    
        setValues((prevValues) => ({
          ...prevValues,
          coverImage: normalizeCoverImage(particularData.coverImage),
          numberOfPages: particularData.numberOfPages ?? null,
          publishedDate: particularData.publishedDate ?? null,
          rating: particularData.rating ?? null,
          link: particularData.link ?? null,
          translations: mergedTranslations,
        }));
    
        initialLangRef.current = true;
        load.loaded(particularData);
      }
    }, [particularData, languages]);


    return (
      <Modal
        isOpen={isModalVisible}
        onRequestClose={closeModal}
        className="modal_body_lg"
        overlayClassName="modal_overlay"
      >
        <div className="box_wrapper">
          <div className="d-flex justify-content-between fw-bold mb-2">
            <h3>Edit Book</h3>
            <LanguageSelector onSelectLanguage={handleSelectLanguage}/>
          </div>
          <EditLoadGate ready={ready} error={load.loadError}>
          <div className="modal_content_lg">
            <div className="row my-2">
            <div className="col-sm-12 col-md-6 mb-3">
              <label className="label">{t("book.authorName")}:</label>
              <SearchAuthor 
                searchAuthorName={searchAuthorName}
                handleAuthorSelect={handleAuthorSelect}
                authorsData={authorsData}
                isShowAuthor={isShowAuthor}
                handleInputChange={handleInputChange}
                handleDropdownFocus={handleDropdownFocus}
                handleRemoveAuthor={handleRemoveAuthor}
                handleGetTranslationByLang={handleGetTranslationByLang}
                selectedLang={selectedLang}
                dropdownRef={dropdownRef}
                isLoading={isLoading}
              />
              {errors && errors.author && <p className="error-message d-block">{errors.author}</p>}
            </div>
              <div className="col-sm-12 col-md-6 mb-4">
                <label className="label">{t("book.bookName")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.bookName")}...`}
                  type="text"
                  name="title"
                  value={handleGetTranslationByLang(selectedLang).title || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {errors && errors.title && <p className="error-message d-block">{errors.title}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-4">
                <label className="label">{t("book.tagline")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.tagline")}...`}
                  type="text"
                  name="tagline"
                  value={handleGetTranslationByLang(selectedLang).tagline || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {/* {errors && errors.tagline && <p className="error-message d-block">{errors.tagline}</p>} */}
              </div>
              <div className="col-sm-12 col-md-3 mb-4">
                <label className="label">{t("book.publishedOn")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.publishedOn")}...`}
                  type="text"
                  maxLength={4}
                  pattern="\d*"
                  name="publishedDate"
                  value={values.publishedDate ? values.publishedDate.split("-")[0] : values.publishedDate}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.publishedDate && <p className="error-message d-block">{errors.publishedDate}</p>}
              </div>
               {/* <div className="col-sm-12 col-md-4 mb-4">
                <label className="label">{t("book.rating")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.rating")}...`}
                  type="float"
                  name="rating"
                  value={values.rating}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {/* {errors && errors.rating && <p className="error-message d-block">{errors.rating}</p>} */}
              {/* </div>  */}
              <div className="col-sm-12 col-md-3 mb-4">
                <label className="label">{t("book.numberOfPages")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.numberOfPages")}...`}
                  type="number"
                  name="numberOfPages"
                  value={values.numberOfPages}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.numberOfPages && <p className="error-message d-block">{errors.numberOfPages}</p>}
              </div>
              <div className="col-sm-12 mb-3">
                <label className="label">{t("book.link")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.link")}...`}
                  type="text"
                  name="link"
                  value={values.link}
                  onChange={(e) => handleAddValues(e.target.name, e.target.value)}
                />
                {errors && errors.link && <p className="error-message d-block">{errors.link}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <div className="d-flex gap-2">
                  <label className="label">{t("book.genre")}:</label>
                  {["Fiction", "NonFiction"].map((genre) => (
                    <div key={genre} className="d-flex align-items-center">
                      <div className="form-label d-flex align-items-center text-muted">
                        {t(`general.${genre}`)}
                        <input
                          className="ms-2"
                          type="radio"
                          name="type"
                          value={genre}
                          checked={handleGetTranslationByLang(selectedLang).type === genre}
                          onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                          style={{ width: "18px", height: "18px" }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <Select
                  className="form-control-md fw-medium p-0"
                  isMulti
                  closeMenuOnSelect={false}
                  name="genre"
                  value={handleGetTranslationByLang(selectedLang).genre?.map((item) => ({ label: item.genreName, value: item.genreId })) || []}
                  onChange={(selectedOptions) => {
                    handleUpdateTranslationField("genre", selectedOptions.map((option) => ({ genreName: option.label, genreId: option.value })), selectedLang);
                  }}
                  options={allGenresOptions?.filter((option) => option.type === handleGetTranslationByLang(selectedLang).type)}
                />
                {errors && errors.genre && <p className="error-message d-block">{errors.genre}</p>}
              </div>
              <div className="col-sm-12 col-md-6 mb-3">
                <label className="label">{t("book.publishedBy")}:</label>
                <input
                  className="form_control"
                  placeholder={`${t("general.enter")} ${t("book.publishedBy")}...`}
                  type="text"
                  name="publishedBy"
                  value={handleGetTranslationByLang(selectedLang).publishedBy || ""}
                  onChange={(e) => handleUpdateTranslationField(e.target.name, e.target.value, selectedLang)}
                />
                {errors && errors.publishedBy && <p className="error-message d-block">{errors.publishedBy}</p>}
              </div>
            <div className="col-sm-12 col-md-8 mb-3">
                <label className="label">About The Book:
                  {" "}<span className="text-muted small fst-italic mt-1">(Please write about the book within given characters — {plainTextLength(handleGetTranslationByLang(selectedLang).summary)}/4500)</span>
                </label>
                <SummaryEditor
                  initialValue={handleGetTranslationByLang(selectedLang).summary || ""}
                  onChange={(content) =>
                    // Store "" rather than Quill's `<p><br></p>`, so an emptied
                    // field is genuinely empty downstream — validation, the
                    // book sync, and the app all read it as absent.
                    handleUpdateTranslationField("summary", isBlankHtml(content) ? "" : content, selectedLang)
                  }
                />
                {errors && errors.summary && <p className="error-message d-block">{errors.summary}</p>}
            </div>
            <div className="col-sm-12 col-md-4 mb-3">
                <PreviewImage
                  label={t("book.coverImage")}
                  image={values.coverImage?.uploadedImageUrl}
                  mediaError={editMediaError}
                  error={errors?.coverImage}
                  uploadMedia={(e) => handleUploadMedia(e)}
                />
              </div>
            </div>
          </div>
          </EditLoadGate>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="btn btn-outline-secondary" onClick={closeModal} disabled={isSubmit}>Cancel</button>
            <Button onClick={handleSubmit} disabled={isSubmit || !ready} isSubmit={isSubmit} label="Submitting ..." text="Update" />
          </div>
        </div>
      </Modal>
    );
};

export default EditCategoryPopup;